import type { KeyboardLayout } from "@/domain/types";
import type { CatalogEntry } from "@/platform/catalog";
import { presetDownload } from "@/platform/catalog/download";
import { blobDownload } from "@/platform/lib/blob-download";
import { DownloadButton } from "./DownloadButton";

/**
 * Download the active Preset's .SC2Hotkeys in the active Keyboard Layout (the lens). US-Qwerty is
 * the vendored bytes verbatim; every other Keyboard Layout is generated from the seed by
 * the conversion engine at click time, so the file follows whatever layout the
 * viewer is on. Lives in the top bar whenever the active SC2 source is a published Preset; an
 * uploaded Profile or a simple Game has none, so it is hidden (the caller renders nothing).
 */
export function PresetDownload({ entry, layout }: { entry: CatalogEntry; layout: KeyboardLayout }) {
  const file = presetDownload(entry, layout);
  // the chip names the artifact type, not the layout-tagged filename: SC2 files differ only by
  // extension, so the extension (leading dot kept) is the identity, stable across every layout.
  const format = file.fileName.slice(file.fileName.lastIndexOf("."));
  // Canonical layout: a plain link to the vendored static asset keeps the bytes byte-faithful.
  if (file.href) {
    return <DownloadButton format={format} href={file.href} fileName={file.fileName} />;
  }
  // Generated layout: build the file in the browser and hand it to a transient download anchor.
  return (
    <DownloadButton format={format} onClick={() => blobDownload(file.fileName, file.text ?? "")} />
  );
}
